import React, { Component } from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Button, FormInput, FormSelect, Card, CardBody, Modal, ModalHeader, ModalBody, Badge } from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';
import './aiKeywordList.scss';

@inject(({ AOA }) => ({
    getAiReviewSentenceList: AOA.getAiReviewSentenceList,
    regenerateAiReviewSentence: AOA.regenerateAiReviewSentence,
    getStoreList: AOA.getStoreList,
}))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@observer
class AiKeywordList extends Component {

    @observable isLoading = false;
    @observable list = { docs: [], totalDocs: 0, totalPages: 1, page: 1, limit: 20 };

    @observable selStoreId = '';
    @observable selShopNo = '';
    @observable keyword = '';
    @observable page = 1;
    @observable limit = 20;

    @observable storeOptions = [];   // [{_id, name, platformId}]
    @observable regenerating = {};   // { [_id]: true } 진행 중 표시

    // 스토어 검색 가능한 셀렉트 — 입력 검색 + 드롭다운
    @observable storeSearchInput = '';
    @observable isStoreDropdownOpen = false;
    storeSelectRef = React.createRef();

    @observable detail = { isOpen: false, row: null };

    componentDidMount() {
        this.loadStoreOptions();
        this.fetchList();
        // 외부 클릭으로 드롭다운 닫기
        document.addEventListener('mousedown', this._onDocMouseDown);
    }
    componentWillUnmount() {
        document.removeEventListener('mousedown', this._onDocMouseDown);
    }
    _onDocMouseDown = (e) => {
        if (this.storeSelectRef.current && !this.storeSelectRef.current.contains(e.target)) {
            this.isStoreDropdownOpen = false;
        }
    };

    @action loadStoreOptions = async () => {
        let _this = this;
        // 일반 + 샘플샵 모두 포함 (운영자 검증용 — 샘플로 알고리즘 테스트 가능)
        this.props.getStoreList({ page: 1, limit: 1000, searchParams: { includeSample: true } }, (res) => {
            if (res && Array.isArray(res.docs)) {
                _this.storeOptions = res.docs.map(s => ({
                    _id: s._id,
                    name: s.name || s.platformId || s._id,
                    platformId: s.platformId,
                    isSample: !!s.isSample,
                    isPaidUser: !!s.isPaidUser
                }));
            }
        });
    };

    @action fetchList = () => {
        let _this = this;
        this.isLoading = true;
        let params = {
            page: this.page,
            limit: this.limit,
            keyword: this.keyword || undefined
        };
        if (this.selStoreId) params.store_id = this.selStoreId;
        if (this.selShopNo) params.shopNo = parseInt(this.selShopNo, 10);
        this.props.getAiReviewSentenceList(params, (res) => {
            _this.isLoading = false;
            if (res && Array.isArray(res.docs)) {
                _this.list = res;
            } else {
                _this.list = { docs: [], totalDocs: 0, totalPages: 1, page: 1, limit: _this.limit };
            }
        });
    };

    @action onChangeStore = (e) => { this.selStoreId = e.target.value; this.page = 1; this.fetchList(); };

    // === 검색 가능한 스토어 셀렉트 핸들러 ===
    @action onStoreInputFocus = () => { this.isStoreDropdownOpen = true; };
    @action onStoreInputChange = (e) => {
        this.storeSearchInput = e.target.value;
        this.isStoreDropdownOpen = true;
    };
    @action onStoreSelect = (storeId, displayLabel) => {
        this.selStoreId = storeId;
        this.storeSearchInput = displayLabel;
        this.isStoreDropdownOpen = false;
        this.page = 1;
        this.fetchList();
    };
    @action clearStoreSelection = () => {
        this.selStoreId = '';
        this.storeSearchInput = '';
        this.isStoreDropdownOpen = false;
        this.page = 1;
        this.fetchList();
    };
    getFilteredStoreOptions = () => {
        const kw = (this.storeSearchInput || '').trim().toLowerCase();
        if (!kw) return this.storeOptions.slice(0, 100);
        return this.storeOptions
            .filter(s => {
                if (s.name && s.name.toLowerCase().indexOf(kw) > -1) return true;
                if (s.platformId && s.platformId.toLowerCase().indexOf(kw) > -1) return true;
                // "샘플" / "sample" / "유료" / "paid" 같은 키워드로 분류 검색 가능
                if ((kw === '샘플' || kw === 'sample') && s.isSample) return true;
                if ((kw === '유료' || kw === 'paid') && s.isPaidUser) return true;
                if ((kw === '무료' || kw === 'free') && !s.isPaidUser && !s.isSample) return true;
                return false;
            })
            .slice(0, 100);
    };
    @action onChangeShopNo = (e) => { this.selShopNo = e.target.value; this.page = 1; this.fetchList(); };
    @action onChangeKeyword = (e) => { this.keyword = e.target.value; };
    @action onSearch = () => { this.page = 1; this.fetchList(); };
    @action onChangePage = (p) => { this.page = p; this.fetchList(); };
    @action onChangeLimit = (e) => { this.limit = parseInt(e.target.value, 10) || 20; this.page = 1; this.fetchList(); };

    @action onRegenerate = (row) => {
        if (row && row.aiEnabled === false) {
            let reason = row.disabledReason || '비활성';
            toast.error('재생성 불가 (' + reason + ') — 유료샵 전환 또는 위젯/리뷰 설정 활성화가 필요합니다.');
            return;
        }
        if (!window.confirm('이 상품 키워드를 다시 생성하시겠습니까?\n(GPT 재호출이 발생하며 약 5~15초 소요됩니다)')) return;
        let _this = this;
        let key = String(row._id);
        this.regenerating[key] = true;
        this.props.regenerateAiReviewSentence({
            store_id: row.store_id,
            shopNo: row.shopNo || 1,
            product_ids: row.product_ids
        }, (res) => {
            _this.regenerating[key] = false;
            if (res && res.data && res.data.ok) {
                toast.success('키워드 재생성이 완료되었습니다.');
                _this.fetchList();
            } else {
                let msg = res && res.data && res.data.message ? res.data.message : '재생성 실패';
                toast.error(msg);
            }
        });
    };

    @action openDetail = (row) => { this.detail = { isOpen: true, row: row }; };
    @action closeDetail = () => { this.detail = { isOpen: false, row: null }; };

    // 대표 상품의 카페24 상품 상세 URL 생성 (storeOptions 의 platformId 기반)
    buildProductUrl = (row) => {
        if (!row || !row.firstProduct || !row.firstProduct.platformProductId) return null;
        const store = this.storeOptions.find(s => String(s._id) === String(row.store_id));
        if (!store || !store.platformId) return null;
        return `https://${store.platformId}.cafe24.com/product/product/${row.firstProduct.platformProductId}/category/`;
    };

    renderPagination = () => {
        const total = this.list.totalPages || 1;
        const cur = this.list.page || 1;
        const pages = [];
        const start = Math.max(1, cur - 2);
        const end = Math.min(total, start + 4);
        for (let i = start; i <= end; i++) pages.push(i);
        return (
            <div className="aikw-pagination">
                <Button size="sm" theme="light" disabled={cur <= 1} onClick={() => this.onChangePage(cur - 1)}>‹</Button>
                {pages.map(p => (
                    <Button key={p} size="sm" theme={p === cur ? 'primary' : 'light'} onClick={() => this.onChangePage(p)}>{p}</Button>
                ))}
                <Button size="sm" theme="light" disabled={cur >= total} onClick={() => this.onChangePage(cur + 1)}>›</Button>
                <span className="aikw-pagination__info">총 {this.list.totalDocs}건</span>
            </div>
        );
    };

    render() {
        const detailRow = this.detail.row;
        return (
            <div className="main-content aikw-page">
                <PageTitle title="AI 리뷰 키워드 검증" subtitle="운영자 도구" />

                {/* 필터 영역 */}
                <Card className="aikw-filter">
                    <CardBody>
                        <div className="aikw-filter__row">
                            <div className="aikw-filter__field">
                                <label>스토어 (이름 또는 platformId 검색)</label>
                                <div className="aikw-store-select" ref={this.storeSelectRef}>
                                    <div className="aikw-store-select__input-wrap">
                                        <FormInput
                                            value={this.storeSearchInput}
                                            onChange={this.onStoreInputChange}
                                            onFocus={this.onStoreInputFocus}
                                            placeholder="전체 스토어 (검색하려면 입력)"
                                            autoComplete="off"
                                        />
                                        {this.selStoreId && (
                                            <button type="button" className="aikw-store-select__clear"
                                                    onClick={this.clearStoreSelection} aria-label="선택 해제">×</button>
                                        )}
                                        <button type="button" className="aikw-store-select__caret"
                                                onClick={() => { this.isStoreDropdownOpen = !this.isStoreDropdownOpen; }}>
                                            <FeatherIcon icon={this.isStoreDropdownOpen ? 'chevron-up' : 'chevron-down'} size="14" />
                                        </button>
                                    </div>
                                    {this.isStoreDropdownOpen && (
                                        <div className="aikw-store-select__dropdown">
                                            <div
                                                className={`aikw-store-select__option ${!this.selStoreId ? 'is-active' : ''}`}
                                                onClick={() => this.onStoreSelect('', '')}
                                            >
                                                <strong>전체 스토어</strong>
                                                <span className="aikw-store-select__hint">선택 해제</span>
                                            </div>
                                            {this.getFilteredStoreOptions().length === 0 ? (
                                                <div className="aikw-store-select__empty">검색 결과 없음</div>
                                            ) : (
                                                this.getFilteredStoreOptions().map(s => {
                                                    const tagSample = s.isSample ? ' [샘플]' : '';
                                                    const tagPay = s.isSample ? '' : (s.isPaidUser ? '' : ' [무료]');
                                                    const label = `${s.name}${s.platformId ? ' (' + s.platformId + ')' : ''}${tagSample}${tagPay}`;
                                                    return (
                                                        <div
                                                            key={s._id}
                                                            className={`aikw-store-select__option ${this.selStoreId === s._id ? 'is-active' : ''}`}
                                                            onClick={() => this.onStoreSelect(s._id, label)}
                                                        >
                                                            <strong>{s.name}</strong>
                                                            {s.isSample && <span className="aikw-store-select__sample">샘플</span>}
                                                            {!s.isSample && !s.isPaidUser && <span className="aikw-store-select__free">무료</span>}
                                                            {s.platformId && <span className="aikw-store-select__platform">{s.platformId}</span>}
                                                        </div>
                                                    );
                                                })
                                            )}
                                            {this.storeOptions.length > 100 && this.getFilteredStoreOptions().length === 100 && (
                                                <div className="aikw-store-select__more">100건만 표시 — 더 좁히려면 검색어 입력</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="aikw-filter__field">
                                <label>shopNo</label>
                                <FormInput type="number" value={this.selShopNo} onChange={this.onChangeShopNo} placeholder="전체" style={{ width: 90 }} />
                            </div>
                            <div className="aikw-filter__field aikw-filter__field--grow">
                                <label>키워드/요약 검색</label>
                                <FormInput value={this.keyword} onChange={this.onChangeKeyword}
                                           placeholder="예) 배송, 디자인, 만족"
                                           onKeyDown={(e) => { if (e.key === 'Enter') this.onSearch(); }} />
                            </div>
                            <div className="aikw-filter__field">
                                <label>페이지당</label>
                                <FormSelect value={this.limit} onChange={this.onChangeLimit} style={{ width: 90 }}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </FormSelect>
                            </div>
                            <div className="aikw-filter__actions">
                                <Button theme="primary" onClick={this.onSearch}>
                                    <FeatherIcon icon="search" size="14" /> 조회
                                </Button>
                                <Button theme="light" onClick={() => { this.keyword = ''; this.selStoreId = ''; this.selShopNo = ''; this.page = 1; this.fetchList(); }}>
                                    <FeatherIcon icon="refresh-ccw" size="14" /> 초기화
                                </Button>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* 리스트 */}
                <Card className="aikw-list">
                    <CardBody>
                        {this.isLoading && <div className="aikw-loading">불러오는 중...</div>}
                        {!this.isLoading && this.list.docs.length === 0 && (
                            <div className="aikw-empty">
                                <FeatherIcon icon="inbox" size="32" />
                                <p>생성된 AI 키워드 데이터가 없습니다.</p>
                            </div>
                        )}
                        {!this.isLoading && this.list.docs.length > 0 && (
                            <table className="aikw-table">
                                <colgroup>
                                    <col width="50px" />
                                    <col />
                                    <col width="180px" />
                                    <col width="70px" />
                                    <col width="70px" />
                                    <col />
                                    <col width="130px" />
                                    <col width="180px" />
                                </colgroup>
                                <thead>
                                <tr>
                                    <th>#</th>
                                    <th>대표 상품</th>
                                    <th>상태</th>
                                    <th>분석<br/>리뷰</th>
                                    <th>키워드<br/>수</th>
                                    <th>한줄 요약 / 키워드 미리보기</th>
                                    <th>마지막 갱신</th>
                                    <th>작업</th>
                                </tr>
                                </thead>
                                <tbody>
                                {this.list.docs.map((row, idx) => {
                                    const num = (this.list.page - 1) * this.list.limit + idx + 1;
                                    const top5 = (row.keywords || []).slice(0, 5);
                                    const isRegen = !!this.regenerating[String(row._id)];
                                    const stale = row.updatedAt && moment().diff(moment(row.updatedAt), 'days') > 30;
                                    const aiOff = row.aiEnabled === false;
                                    return (
                                        <tr key={row._id} className={aiOff ? 'is-ai-disabled' : ''}>
                                            <td>{num}</td>
                                            <td>
                                                {row.firstProduct ? (() => {
                                                    const purl = this.buildProductUrl(row);
                                                    const inner = (
                                                        <>
                                                            {row.firstProduct.image && <img src={row.firstProduct.image} alt="" />}
                                                            <div className="aikw-product__info">
                                                                <div className="aikw-product__name">
                                                                    {row.firstProduct.productName || '(상품명 없음)'}
                                                                    {purl && <FeatherIcon icon="external-link" size="11" className="aikw-product__ext" />}
                                                                </div>
                                                                <div className="aikw-product__meta">
                                                                    {row.firstProduct.platformProductId && <span>PID: {row.firstProduct.platformProductId}</span>}
                                                                    {row.product_idsCount > 1 && <span> · 외 {row.product_idsCount - 1}건 그룹</span>}
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                    return purl ? (
                                                        <a className="aikw-product aikw-product--link" href={purl} target="_blank" rel="noopener noreferrer" title="새 창에서 상품 페이지 열기">{inner}</a>
                                                    ) : (
                                                        <div className="aikw-product">{inner}</div>
                                                    );
                                                })() : (
                                                    <span className="aikw-product--missing">상품 정보 없음 ({row.product_idsCount}건)</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="aikw-status">
                                                    <span className={`aikw-status__pill aikw-status__pill--${row.aiEnabled ? 'on' : 'off'}`}>
                                                        <i /> {row.aiEnabled ? '활성' : '비활성'}
                                                    </span>
                                                    {row.isSample ? (
                                                        <span className="aikw-status__pill aikw-status__pill--sample" title="샘플 스토어">
                                                            <i /> 샘플
                                                        </span>
                                                    ) : (
                                                        <span className={`aikw-status__pill aikw-status__pill--${row.isPaidUser ? 'paid' : 'free'}`}
                                                              title={row.isPaidUser ? '유료 스토어' : '무료 스토어'}>
                                                            <i /> {row.isPaidUser ? '유료' : '무료'}
                                                        </span>
                                                    )}
                                                    <span className={`aikw-status__pill aikw-status__pill--${row.widgetOn ? 'widget-on' : 'widget-off'}`}
                                                          title={row.widgetOn ? '위젯/리뷰 설정 중 하나 이상 ON' : '위젯·리뷰 설정 모두 OFF'}>
                                                        <i /> 설정 {row.widgetOn ? 'ON' : 'OFF'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="text-center">{row.reviewIdsCount}</td>
                                            <td className="text-center"><strong>{row.keywordCount}</strong></td>
                                            <td>
                                                {row.analysis && <div className="aikw-analysis">{row.analysis}</div>}
                                                {top5.length > 0 && (
                                                    <div className="aikw-keyword-preview">
                                                        {top5.map(k => (
                                                            <span key={k.keyword} className="aikw-chip" title={`${k.reviewCount}건`}>
                                                                {k.keyword}
                                                                <em>{k.reviewCount}</em>
                                                            </span>
                                                        ))}
                                                        {row.keywordCount > 5 && <span className="aikw-chip-more">+{row.keywordCount - 5}</span>}
                                                    </div>
                                                )}
                                                {!row.analysis && top5.length === 0 && (
                                                    <span className="aikw-empty-text">데이터 없음</span>
                                                )}
                                            </td>
                                            <td>
                                                {row.updatedAt && (
                                                    <div>
                                                        <div>{moment(row.updatedAt).format('YYYY-MM-DD HH:mm')}</div>
                                                        {stale && <Badge theme="warning" style={{ marginTop: 2 }}>1개월 경과</Badge>}
                                                        {aiOff && <Badge theme="secondary" style={{ marginTop: 2 }}>{row.disabledReason ? `비활성 · ${row.disabledReason}` : 'AI 키워드 비활성'}</Badge>}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <Button size="sm" theme="light" onClick={() => this.openDetail(row)} style={{ marginRight: 4 }}>
                                                    <FeatherIcon icon="eye" size="13" /> 상세
                                                </Button>
                                                <Button size="sm" theme={aiOff ? 'light' : 'primary'}
                                                        onClick={() => this.onRegenerate(row)}
                                                        disabled={isRegen || aiOff}
                                                        title={aiOff ? `재생성 불가 (${row.disabledReason || '비활성'}) — 유료샵 + 위젯/리뷰 설정 활성화 필요` : ''}>
                                                    {isRegen ? '생성 중...' : (<><FeatherIcon icon="refresh-cw" size="13" /> 재생성</>)}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}
                        {!this.isLoading && this.list.docs.length > 0 && this.renderPagination()}
                    </CardBody>
                </Card>

                {/* 상세 모달 */}
                <Modal open={this.detail.isOpen} toggle={this.closeDetail} size="lg">
                    <ModalHeader toggle={this.closeDetail}>AI 키워드 상세</ModalHeader>
                    <ModalBody>
                        {detailRow && (
                            <div className="aikw-detail">
                                {detailRow.firstProduct && (() => {
                                    const purl = this.buildProductUrl(detailRow);
                                    return (
                                        <div className="aikw-detail__product">
                                            {detailRow.firstProduct.image && <img src={detailRow.firstProduct.image} alt="" />}
                                            <div>
                                                <h5>
                                                    {purl ? (
                                                        <a href={purl} target="_blank" rel="noopener noreferrer" className="aikw-detail__product-link">
                                                            {detailRow.firstProduct.productName}
                                                            <FeatherIcon icon="external-link" size="13" style={{ marginLeft: 4 }} />
                                                        </a>
                                                    ) : detailRow.firstProduct.productName}
                                                </h5>
                                                {detailRow.firstProduct.platformProductId && <p>PID: {detailRow.firstProduct.platformProductId}</p>}
                                                <p className="text-muted" style={{ fontSize: 12 }}>
                                                    product_ids: {detailRow.product_idsCount}건 · 분석 리뷰: {detailRow.reviewIdsCount}건 ·
                                                    갱신: {detailRow.updatedAt ? moment(detailRow.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <h6 style={{ marginTop: 16 }}>한줄 요약 (analysis)</h6>
                                <div className="aikw-detail__analysis">
                                    {detailRow.analysis || <em className="text-muted">생성되지 않음 (구버전 데이터일 수 있습니다 — 재생성 권장)</em>}
                                </div>

                                <h6 style={{ marginTop: 16 }}>키워드 ({detailRow.keywordCount}개)</h6>
                                <div className="aikw-detail__keywords">
                                    {detailRow.keywords.length === 0 && <em className="text-muted">키워드 없음</em>}
                                    {detailRow.keywords
                                        .slice()
                                        .sort((a, b) => b.reviewCount - a.reviewCount)
                                        .map(k => (
                                            <div key={k.keyword} className="aikw-detail__kw">
                                                <span className="aikw-detail__kw-name">{k.keyword}</span>
                                                <span className="aikw-detail__kw-count">{k.reviewCount}건</span>
                                                <span className="aikw-detail__kw-bar">
                                                    <span style={{ width: `${Math.min(100, (k.reviewCount / Math.max(1, detailRow.reviewIdsCount)) * 100)}%` }} />
                                                </span>
                                            </div>
                                        ))}
                                </div>

                                <div style={{ marginTop: 16, textAlign: 'right' }}>
                                    <Button theme="light" onClick={this.closeDetail}>닫기</Button>
                                    <Button theme="primary" style={{ marginLeft: 6 }}
                                            disabled={!!this.regenerating[String(detailRow._id)]}
                                            onClick={() => this.onRegenerate(detailRow)}>
                                        {this.regenerating[String(detailRow._id)] ? '생성 중...' : (<><FeatherIcon icon="refresh-cw" size="13" /> 재생성</>)}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

export default AiKeywordList;
